import { buildImageKitUrl } from '@/app/components/media/app-image/shared/imagekit-url';
import {
  SPARTAN_PRODUCT_EXTERIOR_THREESIXTY_IDLE_PREFETCH_RADIUS,
  SPARTAN_PRODUCT_EXTERIOR_THREESIXTY_INTERACTIVE_FRAME_QUALITY,
  SPARTAN_PRODUCT_EXTERIOR_THREESIXTY_MAX_CACHED_FRAME_COUNT,
  SPARTAN_PRODUCT_EXTERIOR_THREESIXTY_MIN_READY_FRAME_COUNT,
  SPARTAN_PRODUCT_EXTERIOR_THREESIXTY_PREFETCH_AHEAD_FRAME_COUNT,
  SPARTAN_PRODUCT_EXTERIOR_THREESIXTY_PREFETCH_BEHIND_FRAME_COUNT,
  SPARTAN_PRODUCT_EXTERIOR_THREESIXTY_READY_WINDOW_RADIUS,
} from '@/app/lcv/components/sections/spartan/products/exterior/threesixty/constants';
import type { SpartanProductExteriorThreesixtyRotationDirection } from '@/app/lcv/components/sections/spartan/products/exterior/threesixty/types';
import {
  getWrapDistance,
  scaleThreesixtyFrameCount,
  wrapThreesixtyIndex,
} from '@/app/lcv/components/sections/spartan/products/exterior/threesixty/utils';

const THREESIXTY_FRAME_PHONE_MAX_VIEWPORT_WIDTH = 600;
const THREESIXTY_FRAME_PHONE_WIDTH_BUCKETS = [828, 960, 1280] as const;
const THREESIXTY_FRAME_MIN_WIDTH = 1280;
// Keep the full animation sequence within 2560px. Only the single stopped
// frame is allowed to upgrade to 2880px on large desktop viewports.
const THREESIXTY_FRAME_WIDTH_BUCKETS = [828, 960, 1280, 1920, 2560] as const;
// Avoid increasing both the frame count and frame width at the 1024px
// mobile/desktop boundary. Full-resolution animation starts at 1280px.
const THREESIXTY_FRAME_COMPACT_VIEWPORT_MAX_WIDTH = 1920;
const THREESIXTY_FRAME_FULL_RESOLUTION_BREAKPOINT = 1280;
const THREESIXTY_FRAME_MAX_DPR = 2;
const THREESIXTY_STOPPED_FRAME_LARGE_DESKTOP_BREAKPOINT = 1440;
const THREESIXTY_STOPPED_FRAME_LARGE_DESKTOP_WIDTH = 2880;
const THREESIXTY_MAX_FALLBACK_FRAME_DISTANCE = 3;

const getThreesixtyPrefetchFrameCounts = (
  length: number,
  maxCachedFrameCount = SPARTAN_PRODUCT_EXTERIOR_THREESIXTY_MAX_CACHED_FRAME_COUNT
) => {
  const desiredAhead = scaleThreesixtyFrameCount(
    SPARTAN_PRODUCT_EXTERIOR_THREESIXTY_PREFETCH_AHEAD_FRAME_COUNT,
    length
  );
  const desiredBehind = scaleThreesixtyFrameCount(
    SPARTAN_PRODUCT_EXTERIOR_THREESIXTY_PREFETCH_BEHIND_FRAME_COUNT,
    length
  );
  const cacheBudget = maxCachedFrameCount - 1;
  const desiredTotal = desiredAhead + desiredBehind;

  if (desiredTotal <= cacheBudget) {
    return { ahead: desiredAhead, behind: desiredBehind };
  }

  const behind = Math.max(
    1,
    Math.round(cacheBudget * (desiredBehind / desiredTotal))
  );

  return { ahead: cacheBudget - behind, behind };
};

const getThreesixtyIdlePrefetchRadius = (
  length: number,
  maxCachedFrameCount = SPARTAN_PRODUCT_EXTERIOR_THREESIXTY_MAX_CACHED_FRAME_COUNT
) =>
  Math.min(
    scaleThreesixtyFrameCount(
      SPARTAN_PRODUCT_EXTERIOR_THREESIXTY_IDLE_PREFETCH_RADIUS,
      length
    ),
    Math.floor((maxCachedFrameCount - 1) / 2)
  );

export const getThreesixtyFrameRequestWidth = (
  viewportWidth: number,
  devicePixelRatio: number
) => {
  if (viewportWidth < THREESIXTY_FRAME_PHONE_MAX_VIEWPORT_WIDTH) {
    const targetWidth = Math.ceil(viewportWidth * (devicePixelRatio || 1));

    return (
      THREESIXTY_FRAME_PHONE_WIDTH_BUCKETS.find(
        (width) => width >= targetWidth
      ) ?? THREESIXTY_FRAME_PHONE_WIDTH_BUCKETS.at(-1)!
    );
  }

  const targetWidth = Math.max(
    THREESIXTY_FRAME_MIN_WIDTH,
    Math.ceil(
      viewportWidth * Math.min(devicePixelRatio || 1, THREESIXTY_FRAME_MAX_DPR)
    )
  );
  const requestWidth =
    THREESIXTY_FRAME_WIDTH_BUCKETS.find((width) => width >= targetWidth) ??
    THREESIXTY_FRAME_WIDTH_BUCKETS.at(-1)!;

  return viewportWidth < THREESIXTY_FRAME_FULL_RESOLUTION_BREAKPOINT
    ? Math.min(requestWidth, THREESIXTY_FRAME_COMPACT_VIEWPORT_MAX_WIDTH)
    : requestWidth;
};

export const getThreesixtyCompleteSequenceRequestWidth = (
  viewportWidth: number,
  devicePixelRatio: number
) => getThreesixtyFrameRequestWidth(viewportWidth, devicePixelRatio);

export const getThreesixtyStoppedFrameRequestWidth = (
  viewportWidth: number,
  devicePixelRatio: number
) =>
  viewportWidth >= THREESIXTY_STOPPED_FRAME_LARGE_DESKTOP_BREAKPOINT
    ? THREESIXTY_STOPPED_FRAME_LARGE_DESKTOP_WIDTH
    : viewportWidth >= 1024
      ? 2560
      : getThreesixtyFrameRequestWidth(viewportWidth, devicePixelRatio);

const getDirectionalDistance = (
  index: number,
  center: number,
  direction: Exclude<SpartanProductExteriorThreesixtyRotationDirection, 0>,
  length: number
) =>
  direction === 1
    ? (index - center + length) % length
    : (center - index + length) % length;

export const getThreesixtyLoadRank = (
  index: number,
  center: number,
  direction: SpartanProductExteriorThreesixtyRotationDirection,
  length: number
) => {
  if (direction === 0) return getWrapDistance(index, center, length);

  const prefetchFrameCounts = getThreesixtyPrefetchFrameCounts(length);

  const ahead = getDirectionalDistance(index, center, direction, length);
  if (ahead === 0 || ahead <= prefetchFrameCounts.ahead) {
    return ahead;
  }

  const behind = getDirectionalDistance(
    index,
    center,
    direction === 1 ? -1 : 1,
    length
  );

  return (
    prefetchFrameCounts.ahead +
    Math.min(
      behind,
      prefetchFrameCounts.behind + getWrapDistance(index, center, length)
    )
  );
};

export const isThreesixtyPrefetchFrame = (
  index: number,
  center: number,
  direction: SpartanProductExteriorThreesixtyRotationDirection,
  length: number,
  maxCachedFrameCount = SPARTAN_PRODUCT_EXTERIOR_THREESIXTY_MAX_CACHED_FRAME_COUNT
) => {
  const idlePrefetchRadius = getThreesixtyIdlePrefetchRadius(
    length,
    maxCachedFrameCount
  );
  if (direction === 0) {
    return getWrapDistance(index, center, length) <= idlePrefetchRadius;
  }

  const prefetchFrameCounts = getThreesixtyPrefetchFrameCounts(
    length,
    maxCachedFrameCount
  );

  const ahead = getDirectionalDistance(index, center, direction, length);
  const behind = getDirectionalDistance(
    index,
    center,
    direction === 1 ? -1 : 1,
    length
  );

  return (
    ahead <= prefetchFrameCounts.ahead || behind <= prefetchFrameCounts.behind
  );
};

export const isThreesixtyAbortError = (error: unknown) =>
  error instanceof DOMException && error.name === 'AbortError';

export const getThreesixtyFrameUrl = (
  src: string,
  requestWidth: number,
  quality = SPARTAN_PRODUCT_EXTERIOR_THREESIXTY_INTERACTIVE_FRAME_QUALITY
) =>
  buildImageKitUrl(src, {
    params: {
      q: quality,
      f: 'auto',
      w: requestWidth,
    },
  });

export const decodeThreesixtyFrame = async (
  image: HTMLImageElement,
  signal: AbortSignal
): Promise<void> => {
  if (signal.aborted) {
    throw new DOMException('Aborted', 'AbortError');
  }
  if (typeof image.decode !== 'function') return;

  let handleAbort: (() => void) | undefined;
  const abortPromise = new Promise<never>((_, reject) => {
    handleAbort = () => {
      image.src = '';
      reject(new DOMException('Aborted', 'AbortError'));
    };
    signal.addEventListener('abort', handleAbort, { once: true });
  });

  try {
    await Promise.race([image.decode(), abortPromise]);
  } finally {
    if (handleAbort) signal.removeEventListener('abort', handleAbort);
  }

  if (signal.aborted) {
    throw new DOMException('Aborted', 'AbortError');
  }
};

export const loadThreesixtyFrame = async (
  src: string,
  signal: AbortSignal,
  priority: 'high' | 'low',
  requestWidth: number,
  quality = SPARTAN_PRODUCT_EXTERIOR_THREESIXTY_INTERACTIVE_FRAME_QUALITY
): Promise<HTMLImageElement> => {
  const img = new Image();
  img.decoding = 'async';
  img.setAttribute('fetchpriority', priority);

  await new Promise<void>((resolve, reject) => {
    const cleanup = () => {
      img.removeEventListener('load', onLoad);
      img.removeEventListener('error', onError);
      signal.removeEventListener('abort', onAbort);
    };
    const onLoad = () => {
      cleanup();
      resolve();
    };
    const onError = () => {
      cleanup();
      reject(new Error(`Failed to load 360 frame: ${src}`));
    };
    const onAbort = () => {
      cleanup();
      img.src = '';
      reject(new DOMException('Aborted', 'AbortError'));
    };

    signal.addEventListener('abort', onAbort);

    if (signal.aborted) {
      onAbort();
      return;
    }

    img.addEventListener('load', onLoad);
    img.addEventListener('error', onError);
    img.src = getThreesixtyFrameUrl(src, requestWidth, quality);

    if (img.complete && img.naturalWidth > 0) {
      cleanup();
      resolve();
    }
  });

  if (signal.aborted) {
    throw new DOMException('Aborted', 'AbortError');
  }

  await decodeThreesixtyFrame(img, signal);

  return img;
};

export const getNearestReadyIndex = (
  index: number,
  readyIndexes: ReadonlySet<number>,
  length: number,
  direction: SpartanProductExteriorThreesixtyRotationDirection = 0,
  maximumDistance = scaleThreesixtyFrameCount(
    THREESIXTY_MAX_FALLBACK_FRAME_DISTANCE,
    length
  )
) => {
  if (length === 0) return null;
  if (readyIndexes.has(index)) return index;
  const maxDistance = Math.min(length - 1, maximumDistance);

  if (direction !== 0) {
    for (let distance = 1; distance <= maxDistance; distance += 1) {
      const previous =
        direction === 1
          ? (index - distance + length) % length
          : (index + distance) % length;
      if (readyIndexes.has(previous)) return previous;
    }

    return null;
  }

  for (let distance = 1; distance <= maxDistance; distance += 1) {
    const forward = (index + distance) % length;
    const backward = (index - distance + length) % length;
    if (readyIndexes.has(forward)) return forward;
    if (readyIndexes.has(backward)) return backward;
  }

  return null;
};

export const trimThreesixtyFrameCache = (
  cache: Map<number, HTMLImageElement>,
  readyIndexes: ReadonlySet<number>,
  protectedIndexes: ReadonlySet<number>,
  maxCount: number
) => {
  if (cache.size <= maxCount) return readyIndexes;

  const nextReady = new Set(readyIndexes);
  for (const index of cache.keys()) {
    if (cache.size <= maxCount) break;
    if (protectedIndexes.has(index)) continue;

    cache.delete(index);
    nextReady.delete(index);
  }

  return nextReady;
};

export const hasReadyFrameWindow = (
  centerIndex: number,
  readyIndexes: ReadonlySet<number>,
  length: number
) => {
  if (length === 0 || !readyIndexes.has(0)) return false;

  const readyWindowRadius = scaleThreesixtyFrameCount(
    SPARTAN_PRODUCT_EXTERIOR_THREESIXTY_READY_WINDOW_RADIUS,
    length
  );

  const requiredIndexes = new Set<number>();
  for (
    let offset = -readyWindowRadius;
    offset <= readyWindowRadius;
    offset += 1
  ) {
    requiredIndexes.add(wrapThreesixtyIndex(centerIndex + offset, length));
  }

  const readyCount = Array.from(requiredIndexes).filter((index) =>
    readyIndexes.has(index)
  ).length;
  const minimumReadyCount = Math.min(
    scaleThreesixtyFrameCount(
      SPARTAN_PRODUCT_EXTERIOR_THREESIXTY_MIN_READY_FRAME_COUNT,
      length
    ),
    requiredIndexes.size
  );

  return readyCount >= minimumReadyCount;
};

export const hasReadyPreviewFrames = (
  currentIndex: number,
  readyIndexes: ReadonlySet<number>,
  previewIndexes: ReadonlySet<number>
) => {
  if (
    previewIndexes.size === 0 ||
    !readyIndexes.has(0) ||
    !readyIndexes.has(currentIndex)
  ) {
    return false;
  }

  return Array.from(previewIndexes).every((index) => readyIndexes.has(index));
};
